import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Mail, Users, Package, ExternalLink, Phone, MessageSquare, CheckCircle } from 'lucide-react';
export function ContactPage() {
  const [activeForm, setActiveForm] = useState<'general' | 'donation'>('general');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would submit the form data
    setFormSubmitted(true);
    // Reset form after 5 seconds for demo purposes
    setTimeout(() => {
      setFormSubmitted(false);
    }, 5000);
  };
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-379">
        <div className="text-center mb-12" data-id="element-380">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-381">
            We'd love to hear from you
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-382">
            Questions, donations, or partnerships—reach out below.
          </p>
        </div>
      </Section>
      {/* Contact Cards Section */}
      <Section background="white" data-id="element-383">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-384">
          {/* General Inquiries */}
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-385">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-386">
              <Mail size={24} className="text-white" data-id="element-387" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-388">
              General Inquiries
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-389">
              Questions about our mission, operations, or how to get involved?
              We're here to help.
            </p>
            <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline font-inter font-medium" data-id="element-390">
              farashamedicalaid@gmail.com
            </a>
          </div>
          {/* Media & Partnerships */}
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-391">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-392">
              <Users size={24} className="text-white" data-id="element-393" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-394">
              Media & Partnerships
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-395">
              For press inquiries or to discuss partnership opportunities, reach
              out via email.
            </p>
            <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline font-inter font-medium" data-id="element-396">
              farashamedicalaid@gmail.com
            </a>
            <p className="font-inter text-neutral-charcoal text-sm mt-2" data-id="element-397">
              Please use the form below and select "Partnership" or "Media" as
              your topic.
            </p>
          </div>
          {/* Donation Drop-offs */}
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-398">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-399">
              <Package size={24} className="text-white" data-id="element-400" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-401">
              Donation Drop-offs
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-402">
              By appointment only via email; no public address. Use the donation
              form below to schedule.
            </p>
            <Button onClick={() => setActiveForm('donation')} variant="secondary" href="#contact-form" data-id="element-403">
              Schedule a Drop-off
            </Button>
          </div>
          {/* Amazon Wish List */}
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-404">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-405">
              <ExternalLink size={24} className="text-white" data-id="element-406" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-407">
              Amazon Wish List
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-408">
              Prefer to ship direct? Shop our most-needed items on our Amazon
              Wish List.
            </p>
            <Button className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-409">
              Open Wish List <ExternalLink size={16} data-id="element-410" />
            </Button>
          </div>
        </div>
        <div className="text-center mb-12" data-id="element-411">
          <div className="inline-flex bg-neutral-offwhite p-1 rounded-lg" data-id="element-412">
            <button className={`px-4 py-2 rounded-md font-inter ${activeForm === 'general' ? 'bg-white shadow-sm' : ''}`} onClick={() => setActiveForm('general')} data-id="element-413">
              General Contact
            </button>
            <button className={`px-4 py-2 rounded-md font-inter ${activeForm === 'donation' ? 'bg-white shadow-sm' : ''}`} onClick={() => setActiveForm('donation')} data-id="element-414">
              Donation Pickup/Drop-off
            </button>
          </div>
        </div>
        {/* Contact Form */}
        <div id="contact-form" className="max-w-3xl mx-auto" data-id="element-415">
          {formSubmitted ? <div className="bg-white p-8 rounded-xl shadow-md text-center" data-id="element-416">
              <CheckCircle size={48} className="text-primary-royal-blue mx-auto mb-4" data-id="element-417" />
              <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-418">
                Thank You!
              </h3>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-419">
                {activeForm === 'general' ? "We've received your message and will get back to you within 2-3 business days." : "We've received your donation pickup/drop-off request and will confirm logistics within 48-72 hours."}
              </p>
              <Button onClick={() => setFormSubmitted(false)} variant="secondary" data-id="element-420">
                Send Another Message
              </Button>
            </div> : activeForm === 'general' ? <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-md" data-id="element-421">
              <h2 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-422">
                General Contact Form
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-423">
                <div data-id="element-424">
                  <label htmlFor="name" className="block font-inter text-neutral-charcoal mb-1" data-id="element-425">
                    Name*
                  </label>
                  <input type="text" id="name" name="name" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-426" />
                </div>
                <div data-id="element-427">
                  <label htmlFor="email" className="block font-inter text-neutral-charcoal mb-1" data-id="element-428">
                    Email Address*
                  </label>
                  <input type="email" id="email" name="email" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-429" />
                </div>
              </div>
              <div className="mb-4" data-id="element-430">
                <label htmlFor="topic" className="block font-inter text-neutral-charcoal mb-1" data-id="element-431">
                  Topic*
                </label>
                <select id="topic" name="topic" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-432">
                  <option value="" data-id="element-433">Select a topic</option>
                  <option value="General" data-id="element-434">General Inquiry</option>
                  <option value="Volunteering" data-id="element-435">Volunteering</option>
                  <option value="Partnership" data-id="element-436">Partnership</option>
                  <option value="Media" data-id="element-437">Media Inquiry</option>
                  <option value="Donation" data-id="element-438">Donation Question</option>
                  <option value="Other" data-id="element-439">Other</option>
                </select>
              </div>
              <div className="mb-4" data-id="element-440">
                <label htmlFor="message" className="block font-inter text-neutral-charcoal mb-1" data-id="element-441">
                  Message*
                </label>
                <textarea id="message" name="message" rows={5} required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="How can we help you?" data-id="element-442"></textarea>
              </div>
              <div className="mb-6" data-id="element-443">
                <label className="flex items-start" data-id="element-444">
                  <input type="checkbox" required className="mt-1 mr-2" data-id="element-445" />
                  <span className="font-inter text-sm text-neutral-charcoal" data-id="element-446">
                    I consent to having this website store my submitted
                    information so they can respond to my inquiry.
                  </span>
                </label>
              </div>
              <Button type="submit" className="w-full" data-id="element-447">
                Send Message
              </Button>
            </form> : <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-md" data-id="element-448">
              <h2 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-449">
                Donation Pickup/Drop-off Request
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-450">
                <div data-id="element-451">
                  <label htmlFor="donor-name" className="block font-inter text-neutral-charcoal mb-1" data-id="element-452">
                    Name*
                  </label>
                  <input type="text" id="donor-name" name="donor-name" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-453" />
                </div>
                <div data-id="element-454">
                  <label htmlFor="donor-email" className="block font-inter text-neutral-charcoal mb-1" data-id="element-455">
                    Email Address*
                  </label>
                  <input type="email" id="donor-email" name="donor-email" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-456" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-457">
                <div data-id="element-458">
                  <label htmlFor="donor-phone" className="block font-inter text-neutral-charcoal mb-1" data-id="element-459">
                    Phone Number*
                  </label>
                  <input type="tel" id="donor-phone" name="donor-phone" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-460" />
                </div>
                <div data-id="element-461">
                  <label htmlFor="donor-location" className="block font-inter text-neutral-charcoal mb-1" data-id="element-462">
                    City/ZIP*
                  </label>
                  <input type="text" id="donor-location" name="donor-location" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-463" />
                </div>
              </div>
              <div className="mb-4" data-id="element-464">
                <label htmlFor="donation-items" className="block font-inter text-neutral-charcoal mb-1" data-id="element-465">
                  Items to Donate* (Please list types of supplies and condition)
                </label>
                <textarea id="donation-items" name="donation-items" rows={3} required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="E.g., 3 boxes of unopened bandages, 2 packages of surgical gloves, etc." data-id="element-466"></textarea>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-467">
                <div data-id="element-468">
                  <label htmlFor="donation-quantity" className="block font-inter text-neutral-charcoal mb-1" data-id="element-469">
                    Quantity/Boxes*
                  </label>
                  <input type="text" id="donation-quantity" name="donation-quantity" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="E.g., 5 medium boxes" data-id="element-470" />
                </div>
                <div data-id="element-471">
                  <label htmlFor="earliest-date" className="block font-inter text-neutral-charcoal mb-1" data-id="element-472">
                    Earliest Available Date*
                  </label>
                  <input type="date" id="earliest-date" name="earliest-date" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-473" />
                </div>
              </div>
              <div className="mb-4" data-id="element-474">
                <label htmlFor="donation-photos" className="block font-inter text-neutral-charcoal mb-1" data-id="element-475">
                  Photos (Optional)
                </label>
                <input type="file" id="donation-photos" name="donation-photos" accept="image/*" multiple className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-476" />
                <p className="text-sm text-neutral-charcoal/70 mt-1" data-id="element-477">
                  You can upload photos of the items you wish to donate.
                </p>
              </div>
              <div className="mb-4" data-id="element-478">
                <label htmlFor="donation-notes" className="block font-inter text-neutral-charcoal mb-1" data-id="element-479">
                  Additional Notes
                </label>
                <textarea id="donation-notes" name="donation-notes" rows={3} className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="Any additional information about your donation, pickup preferences, or special handling instructions." data-id="element-480"></textarea>
              </div>
              <div className="mb-6" data-id="element-481">
                <label className="flex items-start" data-id="element-482">
                  <input type="checkbox" required className="mt-1 mr-2" data-id="element-483" />
                  <span className="font-inter text-sm text-neutral-charcoal" data-id="element-484">
                    I confirm that all items are unopened, unexpired, and in
                    good condition. I understand that a specific drop-off
                    location will be provided after my request is reviewed.
                  </span>
                </label>
              </div>
              <Button type="submit" className="w-full" data-id="element-485">
                Submit Request
              </Button>
            </form>}
        </div>
      </Section>
      {/* Other Ways to Connect */}
      <Section background="sand" data-id="element-486">
        <div className="text-center mb-12" data-id="element-487">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-488">
            Other Ways to Connect
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-489">
            Follow us on social media for updates, impact stories, and
            opportunities to get involved.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-490">
          {/* Social Media */}
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-491">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-492">
              Follow Us
            </h3>
            <div className="space-y-4" data-id="element-493">
              <a href="https://www.instagram.com/farasha.medical/" target="_blank" rel="noopener noreferrer" className="flex items-center font-inter text-neutral-charcoal hover:text-primary-royal-blue transition-colors" data-id="element-494">
                <div className="bg-primary-royal-blue/10 p-2 rounded-full mr-3" data-id="element-495">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-royal-blue" data-id="element-496">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-497"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-498"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-499"></line>
                  </svg>
                </div>
                Instagram: @farasha.medical
              </a>
              <a href="https://www.facebook.com/people/Farasha-Medical-Aid/61577192704150/" target="_blank" rel="noopener noreferrer" className="flex items-center font-inter text-neutral-charcoal hover:text-primary-royal-blue transition-colors" data-id="element-500">
                <div className="bg-primary-royal-blue/10 p-2 rounded-full mr-3" data-id="element-501">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-royal-blue" data-id="element-502">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" data-id="element-503"></path>
                  </svg>
                </div>
                Facebook: Farasha Medical Aid
              </a>
              <div className="pt-4" data-id="element-504">
                <p className="font-inter text-neutral-charcoal mb-2" data-id="element-505">
                  Follow us for:
                </p>
                <ul className="list-disc pl-5 space-y-1 font-inter text-neutral-charcoal" data-id="element-506">
                  <li data-id="element-507">Impact stories and updates</li>
                  <li data-id="element-508">Volunteer opportunities</li>
                  <li data-id="element-509">Educational content about global health</li>
                  <li data-id="element-510">Community events and supply drives</li>
                </ul>
              </div>
            </div>
          </div>
          {/* Newsletter */}
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-511">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-512">
              Subscribe to Our Newsletter
            </h3>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-513">
              Receive quarterly updates on our work, impact stories, and ways to
              get involved. We respect your privacy and will never share your
              information.
            </p>
            <form className="space-y-3" data-id="element-514">
              <div data-id="element-515">
                <input type="email" placeholder="Your email address" className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" required data-id="element-516" />
              </div>
              <div data-id="element-517">
                <label className="flex items-start" data-id="element-518">
                  <input type="checkbox" required className="mt-1 mr-2" data-id="element-519" />
                  <span className="font-inter text-sm text-neutral-charcoal" data-id="element-520">
                    I consent to receiving emails from Farasha Medical Aid. I
                    understand I can unsubscribe at any time.
                  </span>
                </label>
              </div>
              <Button type="submit" className="w-full" data-id="element-521">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </Section>
      {/* Important Notice */}
      <Section background="white" data-id="element-522">
        <div className="max-w-4xl mx-auto" data-id="element-523">
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-524">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-525">
              Important Notice About Donations
            </h3>
            <div className="flex items-start" data-id="element-526">
              <div className="bg-primary-royal-blue rounded-full p-2 mr-4 flex-shrink-0 mt-1" data-id="element-527">
                <MessageSquare size={18} className="text-white" data-id="element-528" />
              </div>
              <p className="font-inter text-neutral-charcoal" data-id="element-529">
                <strong data-id="element-530">We do not publish a drop-off address.</strong> All
                drop-offs are scheduled by appointment only via email at{' '}
                <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-531">
                  farashamedicalaid@gmail.com
                </a>
                . This helps us ensure we have volunteers available to receive
                and process donations properly. Thank you for your
                understanding.
              </p>
            </div>
          </div>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-532">
        <div className="text-center" data-id="element-533">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-534">
            Ready to make an impact?
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-535">
            Whether through donations, volunteering, or partnerships, your
            support helps us bridge healthcare gaps and serve underserved
            communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-536">
            <Button href="/donate" size="lg" data-id="element-537">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-538">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}